import React from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLanguage } from "@/contexts/LanguageContext";
import PlannedInvestmentsBarChart from "./PlannedInvestmentsBarChart";
import DocumentedMeasuresBarChart from "./DocumentedMeasuresBarChart";
import PlannedMeasuresList from "./PlannedMeasuresList";
import DocumentedMeasuresList from "./DocumentedMeasuresList";

interface ProjectMeasuresTabsProps {
  plannedMeasurements: any[];
  documentedMeasurements: any[];
  isLoading: boolean;
  customerColor: string;
}

const ProjectMeasuresTabs: React.FC<ProjectMeasuresTabsProps> = ({
  plannedMeasurements,
  documentedMeasurements,
  isLoading,
  customerColor,
}) => {
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div
          className="animate-spin h-6 w-6 border-2 rounded-full border-t-transparent"
          style={{ borderColor: customerColor }}
        ></div>
      </div>
    );
  }

  const hasPlannedMeasures = plannedMeasurements.length > 0;
  const hasDocumentedMeasures = documentedMeasurements.length > 0;

  // If no measures at all, show empty state
  if (!hasPlannedMeasures && !hasDocumentedMeasures) {
    return (
      <div className="text-sm text-center text-muted-foreground py-4">
        {t("dashboard.noMeasures")}
      </div>
    );
  }

  // If only one type of measures exists, show without tabs
  if (hasPlannedMeasures && !hasDocumentedMeasures) {
    return (
      <div className="space-y-4">
        <div className="bg-white p-4 pb-20 rounded-lg border mb-4">
          <h5 className="text-sm font-medium mb-2">
            {t("dashboard.plannedInvestments")}
          </h5>
          <div className="h-96 pb-10">
            <PlannedInvestmentsBarChart
              plannedMeasurements={plannedMeasurements}
            />
          </div>
        </div>

        <PlannedMeasuresList plannedMeasurements={plannedMeasurements} />
      </div>
    );
  }

  if (!hasPlannedMeasures && hasDocumentedMeasures) {
    return (
      <div className="space-y-4">
        <div className="bg-white p-4 pb-20 rounded-lg border mb-4">
          <h5 className="text-sm font-medium mb-2">
            {t("dashboard.documentedMeasuresTitle")}
          </h5>
          <div className="h-96">
            <DocumentedMeasuresBarChart
              documentedMeasurements={documentedMeasurements}
            />
          </div>
        </div>

        <DocumentedMeasuresList
          documentedMeasurements={documentedMeasurements}
        />
      </div>
    );
  }

  // If both types exist, show tabs
  return (
    <Tabs defaultValue="planned" className="w-full">
      <TabsList className="grid grid-cols-2 mb-4">
        <TabsTrigger value="planned">
          {t("dashboard.plannedMeasures")}
        </TabsTrigger>
        <TabsTrigger value="documented">
          {t("dashboard.documentedMeasures")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="planned" className="space-y-4">
        <div className="bg-white p-4 pb-20 rounded-lg border mb-4">
          <h5 className="text-sm font-medium mb-2">
            {t("dashboard.plannedInvestments")}
          </h5>
          <div className="h-96 pb-10">
            <PlannedInvestmentsBarChart
              plannedMeasurements={plannedMeasurements}
            />
          </div>
        </div>

        <PlannedMeasuresList plannedMeasurements={plannedMeasurements} />
      </TabsContent>

      <TabsContent value="documented" className="space-y-4">
        <div className="bg-white p-4 pb-20 rounded-lg border mb-4">
          <h5 className="text-sm font-medium mb-2">
            {t("dashboard.documentedMeasuresTitle")}
          </h5>
          <div className="h-96">
            <DocumentedMeasuresBarChart
              documentedMeasurements={documentedMeasurements}
            />
          </div>
        </div>

        <DocumentedMeasuresList
          documentedMeasurements={documentedMeasurements}
        />
      </TabsContent>
    </Tabs>
  );
};

export default ProjectMeasuresTabs;
